import {
  analyseRoad,
  collectHazards,
  deriveStarTargets,
  RoadWriter,
} from "./road-authoring.js";
import { roadDifficulty, WORLD_ORDER } from "./difficulty.js";
import { buildGauntletRoad } from "./gauntlet-roads.js";
import { getCalibratedTarget } from "./scoring.js";
import { buildWorldRoad } from "./world-roads.js";

const ROAD_SPECS = {
  ju1: {
    desc: "A broad canopy opener with readable bends, split trees and short jump chains.",
    target: { star3: 9.25, star2: 12 },
  },
  ju2: {
    desc: "Root bridges, tree slaloms and stepped landings over the jungle void.",
    target: { star3: 12.75, star2: 16.25 },
  },
  ju3: {
    desc: "Climb through temple terraces, broken arches and precision gaps.",
    target: { star3: 15.25, star2: 19.5 },
  },
  ju4: {
    desc: "Two risky routes weave around ruins before a long canopy sprint.",
    target: { star3: 16.75, star2: 21.5 },
  },
  ju5: {
    desc: "Circle the Mayhem Tree, climb its crown and survive the portal approach.",
    target: { star3: 25, star2: 32.5 },
  },
};

function canopyRun() {
  const w = new RoadWriter("ju1");
  w.add("nnnnn", 8)
    .markLast({ type: "start-totem", side: -1 })
    .curve("nnnnn", 9, 0, 1.9)
    .tree("nnxnn", [2], { _offset: 1.9 })
    .add("nn.nn", 3, { _offset: 1.9 })
    .curve("nnnnn", 8, 1.9, 0)
    .add(".nnn.", 4)
    .jump(".nnn.", 1)
    .add("nnnnn", 5)
    .markLast({ type: "waterfall", side: 1, distance: 10 })
    .curve("nnnn.", 8, 0, -1.8)
    .tree("nxnn.", [1], { _offset: -1.8 })
    .add("n.nnn", 3, { _offset: -1.8 })
    .curve("nnnnn", 8, -1.8, 0)
    .add("nndnn", 2)
    .add("nnnnn", 4)
    .markLast({ type: "ruin-arch", clearance: 6.5 })
    .add(".uuu.", 2)
    .add("nnnnn", 5)
    .jump("nnnnn", 1)
    .finish();
  return w.rows;
}

function rootTangle() {
  const w = new RoadWriter("ju2");
  w.add("nnnnn", 9)
    .markLast({ type: "root-bridge", side: -1 })
    .curve(".nnn.", 10, 0, 2.1)
    .tree(".nxn.", [2], { _offset: 2.1 })
    .add(".n.n.", 4, { _offset: 2.1 })
    .jump(".nn..", 1, { _offset: 2.1 })
    .curve(".nnn.", 9, 2.1, -1.7)
    .add("nn.nn", 4, { _offset: -1.7 })
    .tree("nx.xn", [1, 3], { _offset: -1.7 })
    .add("n...n", 4, { _offset: -1.7 })
    .add("bb.bb", 1, { _offset: -1.7 })
    .gap(2, { _offset: -1.7 })
    .add("nn.nn", 3, { _offset: -1.7 })
    .bendAndRamp(".nnn.", 12, -1.7, 0, 0, 2.2)
    .add(".ccc.", 3, { _elevation: 2.2 })
    .add(".nnn.", 4, { _elevation: 2.2 })
    .markLast({ type: "canopy-gate", clearance: 7.5 })
    .jump(".nnn.", 2, { _elevation: 2.2 })
    .ramp("nnnnn", 12, 2.2, 0)
    .tree("xnnnx", [0, 4])
    .add(".uuu.", 2)
    .curve("nnnnn", 11, 0, 1.8)
    .add("ndndn", 2, { _offset: 1.8 })
    .curve(".nnn.", 10, 1.8, 0)
    .jump(".nnn.", 1)
    .add("nnnnn", 6)
    .finish();
  return w.rows;
}

function templeSteps() {
  const w = new RoadWriter("ju3");
  w.add("nnnnn", 10)
    .markLast({ type: "temple-court", side: 1 })
    .bendAndRamp("nnnnn", 14, 0, -2, 0, 2.6)
    .tree("nnxnn", [2], { _offset: -2, _elevation: 2.6 })
    .add("nn.nn", 4, { _offset: -2, _elevation: 2.6 })
    .jump("nn.nn", 1, { _offset: -2, _elevation: 2.6 })
    .curve(".nnn.", 10, -2, 1.8, { _elevation: 2.6 })
    .add(".ddd.", 2, { _offset: 1.8, _elevation: 2.6 })
    .add(".nnn.", 4, { _offset: 1.8, _elevation: 2.6 })
    .markLast({ type: "ruin-arch", clearance: 8 })
    .ramp(".nnn.", 12, 2.6, 4.6, { _offset: 1.8 })
    .add("..n..", 4, { _offset: 1.8, _elevation: 4.6 })
    .add("..b..", 1, { _offset: 1.8, _elevation: 4.6 })
    .gap(2, { _offset: 1.8, _elevation: 4.6 })
    .add(".nnn.", 3, { _offset: 1.8, _elevation: 3.8 })
    .bendAndRamp("nnnnn", 14, 1.8, 0, 3.8, 1.2)
    .add("c.n.c", 3, { _elevation: 1.2 })
    .add("n.n.n", 4, { _elevation: 1.2 })
    .jump("n.n.n", 2, { _elevation: 1.2 })
    .add("nnnnn", 6, { _elevation: 1.2 })
    .markLast({ type: "waterfall", side: -1, distance: 8 })
    .curve("nnnn.", 12, 0, -2.1, { _elevation: 1.2 })
    .tree("nxnn.", [1], { _offset: -2.1, _elevation: 1.2 })
    .add("n.n..", 4, { _offset: -2.1, _elevation: 1.2 })
    .jump("nnn..", 2, { _offset: -2.1, _elevation: 1.2 })
    .bendAndRamp(".nnn.", 12, -2.1, 0, 1.2, 0)
    .add(".uuu.", 2)
    .add("nnnnn", 7)
    .jump("nnnnn", 2)
    .finish();
  return w.rows;
}

function vineVerge() {
  const w = new RoadWriter("ju4");
  w.add("nnnnn", 10)
    .markLast({ type: "vine-wall", side: -1 })
    .curve("nnnnn", 12, 0, 2)
    .tree("xnnnx", [0, 4], { _offset: 2 })
    .add("nn.nn", 5, { _offset: 2 })
    .add("bb.bb", 1, { _offset: 2 })
    .gap(2, { _offset: 2 })
    .add("nn.nn", 4, { _offset: 2 })
    .curve("nn.nn", 14, 2, -2)
    .tree("nx.xn", [1, 3], { _offset: -2 })
    .add("n...n", 4, { _offset: -2 })
    .add("b...b", 1, { _offset: -2 })
    .gap(3, { _offset: -2 })
    .add("n...n", 3, { _offset: -2 })
    .bendAndRamp("nn.nn", 14, -2, 0, 0, 2.8)
    .add("cc.cc", 4, { _elevation: 2.8 })
    .add("nn.nn", 5, { _elevation: 2.8 })
    .markLast({ type: "temple-court", side: -1 })
    .jump("nn.nn", 2, { _elevation: 2.8 })
    .curve(".nnn.", 13, 0, 2, { _elevation: 2.8 })
    .add(".dnd.", 3, { _offset: 2, _elevation: 2.8 })
    .tree(".nxn.", [2], { _offset: 2, _elevation: 2.8 })
    .add(".n.n.", 4, { _offset: 2, _elevation: 2.8 })
    .jump(".n.n.", 2, { _offset: 2, _elevation: 2.8 })
    .bendAndRamp("nnnnn", 16, 2, -1.8, 2.8, 0.8)
    .add("nndnn", 3, { _offset: -1.8, _elevation: 0.8 })
    .add(".uuu.", 2, { _offset: -1.8, _elevation: 0.8 })
    .curve(".nnn.", 12, -1.8, 0, { _elevation: 0.8 })
    .markLast({ type: "ruin-arch", clearance: 8.5 })
    .jump(".nnn.", 3, { _elevation: 0.8 })
    .ramp("nnnnn", 12, 0.8, 0)
    .tree("xnnnx", [0, 4])
    .add("nnnnn", 7)
    .jump("nnnnn", 2)
    .finish();
  return w.rows;
}

function mayhemHeart() {
  const w = new RoadWriter("ju5");
  w.add("nnnnn", 11)
    .markLast({ type: "giant-tree", side: 1, scale: 1.4 })
    .curve(".nnn.", 14, 0, 2.2)
    .tree(".nxn.", [2], { _offset: 2.2 })
    .add(".n.n.", 5, { _offset: 2.2 })
    .jump(".n.n.", 2, { _offset: 2.2 })
    .bendAndRamp("nn.nn", 16, 2.2, -2.2, 0, 2.4)
    .tree("nx.xn", [1, 3], { _offset: -2.2, _elevation: 2.4 })
    .add("n...n", 5, { _offset: -2.2, _elevation: 2.4 })
    .add("b...b", 1, { _offset: -2.2, _elevation: 2.4 })
    .gap(3, { _offset: -2.2, _elevation: 2.4 })
    .add("n...n", 3, { _offset: -2.2, _elevation: 2.4 })
    .curve("nn.nn", 15, -2.2, 1.9, { _elevation: 2.4 })
    .add("cc.cc", 4, { _offset: 1.9, _elevation: 2.4 })
    .jump("nn.nn", 2, { _offset: 1.9, _elevation: 2.4 })
    .bendAndRamp(".nnn.", 18, 1.9, 0, 2.4, 5.2)
    .markLast({ type: "canopy-gate", clearance: 9 })
    .add("..n..", 5, { _elevation: 5.2 })
    .add("..b..", 1, { _elevation: 5.2 })
    .gap(3, { _elevation: 5.2 })
    .add(".nn..", 3, { _elevation: 4.4 })
    .curve(".nnn.", 14, 0, -2, { _elevation: 4.4 })
    .tree(".nxn.", [2], { _offset: -2, _elevation: 4.4 })
    .add(".n.n.", 4, { _offset: -2, _elevation: 4.4 })
    .jump(".n.n.", 3, { _offset: -2, _elevation: 4.4 })
    .bendAndRamp("nn.nn", 18, -2, 2, 4.4, 2.2)
    .add("dd.dd", 3, { _offset: 2, _elevation: 2.2 })
    .add("nn.nn", 5, { _offset: 2, _elevation: 2.2 })
    .markLast({ type: "waterfall", side: 1, distance: 7 })
    .add("bb.bb", 1, { _offset: 2, _elevation: 2.2 })
    .gap(3, { _offset: 2, _elevation: 2.2 })
    .add("nn.nn", 3, { _offset: 2, _elevation: 2.2 })
    .curve("n.n.n", 16, 2, -1.8, { _elevation: 2.2 })
    .tree("x.n.x", [0, 4], { _offset: -1.8, _elevation: 2.2 })
    .add("..n..", 5, { _offset: -1.8, _elevation: 2.2 })
    .jump("..n..", 3, { _offset: -1.8, _elevation: 2.2 })
    .bendAndRamp(".nnn.", 16, -1.8, 0, 2.2, 0.8)
    .add(".ccc.", 4, { _elevation: 0.8 })
    .add(".uuu.", 2, { _elevation: 0.8 })
    .add(".nnn.", 6, { _elevation: 0.8 })
    .markLast({ type: "temple-court", side: 1 })
    .jump(".nnn.", 3, { _elevation: 0.8 })
    .ramp("nnnnn", 12, 0.8, 0)
    .tree("xnxnx", [0, 2, 4])
    .add(".n.n.", 5)
    .add(".b.b.", 1)
    .gap(3)
    .add(".n.n.", 3)
    .curve("nnnnn", 12, 0, 1.7)
    .curve("nnnnn", 12, 1.7, 0)
    .add("nnnnn", 6)
    .finish();
  return w.rows;
}

const JUNGLE_BUILDERS = {
  ju1: canopyRun,
  ju2: rootTangle,
  ju3: templeSteps,
  ju4: vineVerge,
  ju5: mayhemHeart,
};

const PREFIX_WORLD = {
  ju: "jungle",
  wa: "waterfall",
  re: "redheat",
  as: "asteroid",
  bl: "blueplanet",
  sa: "satellite",
  mi: "misty",
  ne: "nebula",
  bo: "boss",
};

function identifyRoad(road) {
  const worldId = PREFIX_WORLD[road.id.slice(0, 2)] || null;
  const roadIndex = Math.max(0, Number.parseInt(road.id.slice(2), 10) - 1);
  return {
    worldId,
    worldIndex: WORLD_ORDER.indexOf(worldId),
    roadIndex,
  };
}

export function enhanceRoad(road) {
  const identity = identifyRoad(road);
  const builder = JUNGLE_BUILDERS[road.id];
  if (builder) {
    const spec = ROAD_SPECS[road.id];
    road.rows = builder();
    road.hazards = [];
    road.desc = spec.desc;
    road.par = spec.target.star3 / 1.18;
    road.star3 = spec.target.star3;
    road.star2 = spec.target.star2;
    road.authored = true;
    road.routeVersion = 3;
  } else {
    const authored =
      identity.worldId === "boss"
        ? buildGauntletRoad(road.id, identity.roadIndex)
        : buildWorldRoad(identity.worldId, road.id, identity.roadIndex);
    if (authored) {
      road.rows = authored.rows;
      road.hazards = collectHazards(road.rows);
      road.desc = authored.desc;
      road.difficulty = roadDifficulty(identity.worldId, identity.roadIndex);
      const targets = deriveStarTargets(road.rows, {
        difficulty: road.difficulty,
        speedMult: road.speedMult,
      });
      const calibrated = getCalibratedTarget(road.id);
      road.par = (calibrated?.star3 ?? targets.star3) / 1.18;
      road.star3 = calibrated?.star3 ?? targets.star3;
      road.star2 = calibrated?.star2 ?? targets.star2;
      road.authored = true;
      road.routeVersion = 3;
    }
  }

  road.difficulty ??= roadDifficulty(identity.worldId, identity.roadIndex);
  const metrics = analyseRoad(road.rows);
  const catalog = (window.__orbitrushRoadCatalog ||= {});
  catalog[road.id] = {
    id: road.id,
    name: road.name,
    desc: road.desc,
    worldId: identity.worldId,
    worldIndex: identity.worldIndex,
    roadIndex: identity.roadIndex,
    difficulty: road.difficulty,
    star3: road.star3,
    star2: road.star2,
    rowCount: metrics.rowCount,
    maxGap: metrics.maxGap,
    hazardCount: road.hazards.length,
    finishTiles: metrics.finishTiles,
    authored: Boolean(road.authored),
    routeVersion: road.routeVersion,
  };
  return road;
}

export function getRoadCatalog() {
  return window.__orbitrushRoadCatalog || {};
}
